/**
 * @fileoverview Guided tour targets and steps (`DASHBOARD_BUILDER_TOUR_STEPS` & `DASHBOARD_TOUR_TARGET`)
 * for the elevated 3-pane Dashboard Builder in both `tenant-web` and `system-web`.
 * @module ui-admin/components/DashboardCanvasBuilder/dashboardTourSteps
 *
 * Responsibilities: - Define `DASHBOARD_TOUR_TARGET` `data-tour` anchor IDs.
 *                   - Define `DASHBOARD_BUILDER_TOUR_STEPS` consumed by `GuidedTour`.
 * Dependencies:     - ../../help/types (TourStep)
 * Architecture:     - Pure static tour metadata; mirrors `builderTourSteps.ts` and `reportTourSteps.ts`.
 * Notes:            - Closes gap G-21.
 */

import type { TourStep } from '../../help/types';

export const DASHBOARD_TOUR_TARGET = {
  addWidget: 'dash-tour-add-widget',
  widgetCard: 'dash-tour-widget-card',
  overflowMenu: 'dash-tour-overflow-menu',
  activationList: 'dash-tour-activation-list',
  propertiesTab: 'dash-tour-properties-tab',
  previewTab: 'dash-tour-preview-tab',
  savePublish: 'dash-tour-save-publish',
  assignmentAccordion: 'dash-tour-assignment-accordion',
} as const;

const sel = (target: string) => `[data-tour="${target}"]`;

export const DASHBOARD_BUILDER_TOUR_STEPS: TourStep[] = [
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.addWidget),
    title: 'Add Widgets to the Canvas',
    body: 'Click "+ Add widget" to open the categorized widget palette drawer (KPIs, Charts, Data Tables, Global Filters, and Maps).',
    placement: 'bottom',
  },
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.widgetCard),
    title: 'Drag, Resize & Select Widgets',
    body: 'Drag widget cards across the 12-column grid, resize their bottom-right handle, or click a card to edit its properties.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.overflowMenu),
    title: 'Widget Actions Menu',
    body: 'Use the ⋮ menu on any widget card to Duplicate, Move Up, Move Down, or Delete the widget.',
    placement: 'left',
  },
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.activationList),
    title: 'Widget Activation Checklist',
    body: 'Toggle widgets on or off without deleting them, or click any row to jump straight to its properties.',
    placement: 'right',
  },
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.propertiesTab),
    title: 'Content & Style Properties',
    body: 'Configure widget titles, data source cubes/measures/dimensions, chart sub-types, and card visual styling.',
    placement: 'left',
  },
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.previewTab),
    title: 'Live Dashboard Preview',
    body: 'Preview your complete dashboard layout with real local Dexie data (or Preview Department data in System Admin).',
    placement: 'left',
  },
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.assignmentAccordion),
    title: 'Assignment & Library Exposure',
    body: 'Assign a published dashboard to the Dashboard area (or System Library), set it as Primary, and control visibility.',
    placement: 'right',
  },
  {
    targetSelector: sel(DASHBOARD_TOUR_TARGET.savePublish),
    title: 'Save, Publish & Archive',
    body: 'Save draft changes locally, publish when ready for users, or archive retired dashboards.',
    placement: 'bottom',
  },
];
